import { Kws, chainOrSuspend, pyNone, pyStr, toJs } from "@Sk";
import PyDefUtils from "PyDefUtils";
import {
    AnvilHookSpec,
    Component,
    ComponentConstructor,
    DropZone,
    HandleInteraction,
    LayoutProperties,
} from "@runtime/components/Component";
import { designerApi } from "@runtime/runner/component-designer-api";
import { getCssPrefix } from "@runtime/runner/legacy-features";
import { editState } from "../edit-state";
import { BASE_MODULES } from "../python-environment";

const { DesignColumnPanel, ColumnPanel } = BASE_MODULES["anvil"];

const getRandomStr = (len: number): string => {
    var r = "";
    for (var i = 0; i < len; i++) {
        r += String.fromCharCode(65 + Math.floor(26 * Math.random()));
    }
    return r;
};

export const mkDesignerHookSpec = (oldHookSpec: AnvilHookSpec): Partial<AnvilHookSpec> => {
    return {
        cleanupLayoutProperties() {
            const oldUpdates = this._anvil.cleanupLayoutProperties(
                (element: HTMLElement) => editState.componentsByDomNode.get(element)?.pyComponent,
                (pyComponent: Component) => editState.namesByComponent.get(pyComponent)
            );
            return {
                propertyUpdates: oldUpdates.newProperties,
                layoutPropertyUpdates: oldUpdates.newLayoutProperties,
            };
        },
        enableDropMode(dropping, flags) {
            const element = this.anvil$hooks.domElement!;
            const { panelId } = this._anvil;
            const dzs: DropZone[] = [];
            const { mkDropzone, maxChildIdxOf, minChildIdxOf, buildGridPath } = this._anvil.designerHelpers;

            if (flags?.allowOtherComponentUpdates) {
                const rows = element.querySelectorAll<HTMLElement>(`.anvil-panel-row.belongs-to-${panelId}`);

                if (rows.length > 1) {
                    // There are multiple rows in this ColumnPanel, so we need to be able to create whole new columns on the left and right.
                    element.classList.add("anvil-column-panel-with-dzs");
                    const leftColDz = mkDropzone();
                    const rightColDz = mkDropzone();
                    leftColDz.classList.add("anvil-outer-dz-left");
                    leftColDz.style.marginLeft = "-8px"; // TODO: Is col padding always 8px? Probably not...
                    leftColDz.style.marginRight = "8px";
                    rightColDz.classList.add("anvil-outer-dz-right");
                    rightColDz.style.marginLeft = "8px";
                    rightColDz.style.marginRight = "-8px";
                    // Insert both at the start so they don't interfere with :last-child test below. They get positioned in the grid correctly anyway.
                    element.insertAdjacentElement("afterbegin", rightColDz);
                    element.insertAdjacentElement("afterbegin", leftColDz);

                    var u: any = {};
                    var newRow = getRandomStr(6);
                    var newCol = getRandomStr(6);
                    element
                        .querySelectorAll<HTMLElement>(
                            "[data-anvil-designer-columnpanel-component].belongs-to-" + this._anvil.panelId
                        )
                        .forEach((ce) => {
                            var gridPos = ce.dataset.anvilDesignerPanelGridPos;
                            const component = editState.componentsByDomNode.get(ce)!;
                            if (component) {
                                u[component.name] = {
                                    layout_properties: {
                                        grid_position: newRow + "," + newCol + " " + gridPos,
                                    },
                                };
                            }
                        });

                    dzs.push({
                        element: leftColDz,
                        dropInfo: {
                            layout_properties: { grid_position: `${newRow},${getRandomStr(6)}` },
                            maxChildIdx: 0,
                            otherComponentUpdates: u,
                        },
                    });
                    const childIdx = maxChildIdxOf(element);
                    dzs.push({
                        element: rightColDz,
                        dropInfo: {
                            layout_properties: { grid_position: `${newRow},${getRandomStr(6)}` },
                            minChildIdx: childIdx,
                            maxChildIdx: childIdx,
                            otherComponentUpdates: u,
                        },
                    });
                }

                for (const row of rows) {
                    // Add dropzone before this row
                    const beforeRowDz = mkDropzone();
                    row.insertAdjacentElement("beforebegin", beforeRowDz);
                    let childIdx = minChildIdxOf(row);
                    if (row === rows[0] && this._anvil.components.length > 3) {
                        // console.log(childIdx);
                        // debugger;
                    }

                    dzs.push({
                        element: beforeRowDz,
                        dropInfo: {
                            layout_properties: {
                                grid_position: `${buildGridPath(row.parentElement!)} ${getRandomStr(6)},${getRandomStr(
                                    6
                                )}`.trim(),
                            },
                            minChildIdx: childIdx,
                            maxChildIdx: childIdx,
                        },
                    });

                    // Add dropzone after this row if required
                    const lastNestedRow = row.matches(":last-child") && row.parentElement!.matches(".anvil-panel-col");
                    const lastTopLevelRow =
                        row.parentElement!.matches(".anvil-panel-section-gutter") &&
                        row.parentElement!.parentElement!.parentElement!.matches(":last-child");
                    if (lastNestedRow || lastTopLevelRow) {
                        const afterRowDz = mkDropzone();
                        row.insertAdjacentElement("afterend", afterRowDz);
                        const childIdx = maxChildIdxOf(row) + 1;
                        dzs.push({
                            element: afterRowDz,
                            dropInfo: {
                                layout_properties: {
                                    grid_position: `${buildGridPath(row.parentElement!)} ${getRandomStr(
                                        6
                                    )},${getRandomStr(6)}`.trim(),
                                },
                                minChildIdx: childIdx,
                                maxChildIdx: childIdx,
                            },
                        });
                    }

                    // Add dropzones before and after each column in this row
                    const cols = [...row.children].filter((c) => c.matches(".anvil-panel-col")) as HTMLElement[];
                    for (const col of cols) {
                        // If there is more than one column in this row, and this column is a leaf node, add dropzones above and below this column.
                        const padding = col.querySelector(`:scope >.${getCssPrefix()}col-padding`);
                        const child = padding?.querySelector(":scope >[data-anvil-designer-columnpanel-component]");
                        if (cols.length > 1 && child?.matches("[data-anvil-designer-columnpanel-component]")) {
                            const component = editState.componentsByDomNode.get(child);

                            const gridPos = buildGridPath(col);
                            const newRow = getRandomStr(6);
                            const newCol = getRandomStr(6);
                            const u = component
                                ? {
                                      [component.name]: {
                                          layout_properties: {
                                              grid_position: `${gridPos} ${getRandomStr(6)},${getRandomStr(6)}`.trim(),
                                          }, // TODO
                                      },
                                  }
                                : {};
                            const topDz = mkDropzone();
                            padding!.insertAdjacentElement("afterbegin", topDz);
                            let childIdx = minChildIdxOf(col);
                            dzs.push({
                                element: topDz,
                                dropInfo: {
                                    layout_properties: { grid_position: `${gridPos} ${newRow},${newCol}`.trim() }, // TODO
                                    minChildIdx: childIdx,
                                    maxChildIdx: childIdx,
                                    otherComponentUpdates: u,
                                },
                            });
                            const bottomDz = mkDropzone();
                            padding!.insertAdjacentElement("beforeend", bottomDz);
                            childIdx = maxChildIdxOf(col) + 1;
                            dzs.push({
                                element: bottomDz,
                                dropInfo: {
                                    layout_properties: { grid_position: `${gridPos} ${newRow},${newCol}`.trim() }, // TODO
                                    minChildIdx: childIdx,
                                    maxChildIdx: childIdx,
                                    otherComponentUpdates: u,
                                },
                            });
                        }

                        const dzElement = mkDropzone();
                        row.insertBefore(dzElement, col);
                        const childIdx = minChildIdxOf(col);
                        dzs.push({
                            element: dzElement,
                            dropInfo: {
                                layout_properties: {
                                    grid_position: `${buildGridPath(row.parentElement)} ${
                                        row.dataset.anvilRowId
                                    },${getRandomStr(6)}`.trim(),
                                },
                                minChildIdx: childIdx,
                                maxChildIdx: childIdx,
                            },
                        });
                    }
                    const finalColumnDz = mkDropzone();
                    row.appendChild(finalColumnDz);
                    childIdx = maxChildIdxOf(row) + 1;
                    dzs.push({
                        element: finalColumnDz,
                        dropInfo: {
                            layout_properties: {
                                grid_position: `${buildGridPath(row.parentElement)} ${
                                    row.dataset.anvilRowId
                                },${getRandomStr(6)}`.trim(),
                            },
                            minChildIdx: childIdx,
                            maxChildIdx: childIdx,
                        },
                    });
                }

                // Finally, if the column panel is empty, add one dropzone
                if (rows.length === 0) {
                    const singleDz = mkDropzone();
                    element.appendChild(singleDz);
                    dzs.push({
                        element: singleDz,
                        dropInfo: {
                            layout_properties: { grid_position: `${getRandomStr(6)},${getRandomStr(6)}` },
                            maxChildIdx: 0,
                        },
                    });
                }
            } else {
                const layoutProps = toJs(dropping.pyLayoutProperties) as LayoutProperties;
                const onlyGridPos = layoutProps["grid_position"];

                const componentsByGridPos: { [gridPos: string]: [Component, number][] } = {};
                (
                    this._anvil.components as {
                        component: Component;
                        layoutProperties: { grid_position: string };
                    }[]
                ).map(({ component, layoutProperties: { grid_position } }, idx) => {
                    componentsByGridPos[grid_position] ||= [];
                    componentsByGridPos[grid_position].push([component, idx]);
                });

                for (const [grid_position, components] of Object.entries(componentsByGridPos)) {
                    if (onlyGridPos && grid_position !== onlyGridPos) {
                        continue;
                    }
                    for (const [c, childIdx] of components) {
                        const element = mkDropzone();
                        c.anvil$hooks.domElement?.insertAdjacentElement("beforebegin", element);
                        dzs.push({
                            element,
                            dropInfo: {
                                layout_properties: { grid_position },
                                minChildIdx: childIdx,
                                maxChildIdx: childIdx,
                            },
                        });
                    }
                    const element = mkDropzone();
                    const [lastComponent, lastChildIdx] = components[components.length - 1];
                    lastComponent.anvil$hooks.domElement?.insertAdjacentElement("afterend", element);
                    const finalChildIdx = lastChildIdx + 1;
                    dzs.push({
                        element,
                        dropInfo: {
                            layout_properties: { grid_position },
                            minChildIdx: finalChildIdx,
                            maxChildIdx: finalChildIdx,
                        },
                    });
                }

                if (dzs.length === 0) {
                    const levels = onlyGridPos?.split(" ");
                    let rowContainer, colContainer;

                    if (levels && levels.length > 0) {
                        for (const level of levels) {
                            const [rowId, colId] = level.split(",");
                            if (this._anvil.rows[rowId]) {
                                rowContainer = this._anvil.rows[rowId];
                            }
                            if (this._anvil.cols[colId]) {
                                colContainer = this._anvil.cols[colId];
                            } else {
                                break;
                            }
                        }
                    }
                    const element = mkDropzone();

                    const aimingForChildIdx = dropping.minChildIdx; // Should be equal to maxChildIdx in a ColumnPanel
                    const insertBeforeChild =
                        aimingForChildIdx != null && this._anvil.components[aimingForChildIdx]?.component;
                    const targetContainer = colContainer?.el || rowContainer?.el || this.anvil$hooks.domElement!;

                    if (insertBeforeChild) {
                        let insertBefore = insertBeforeChild.anvil$hooks.domElement!;

                        while (insertBefore) {
                            if (insertBefore.parentElement === targetContainer) {
                                break;
                            }
                            insertBefore = insertBefore.parentElement;
                            if (insertBefore === this.anvil$hooks.domElement!) {
                                insertBefore = null;
                            }
                        }

                        if (insertBefore) {
                            insertBefore.insertAdjacentElement("beforebegin", element);
                        } else {
                            targetContainer.insertAdjacentElement("beforeend", element);
                        }
                    } else {
                        targetContainer.insertAdjacentElement("beforeend", element);
                    }

                    // if (colContainer && aimingForChildIdx != null) {
                    //     const components = [...colContainer.el.querySelectorAll(`[data-anvil-designer-columnpanel-component].belongs-to-${panelId}`)];
                    //     const insertBeforeComponent = components.find((c) => c.dataset.anvilDesignerPanelChildIdx") === aimingForChildIdx);
                    //     if (insertBeforeComponent) {
                    //         insertBeforeComponent.insertAdjacentElement("beforebegin", element);
                    //     } else {
                    //         colContainer.insertAdjacentElement("beforeend", element);
                    //     }
                    // } else {
                    //     this.anvil$hooks.domElement!.insertAdjacentElement("beforeend", element);
                    // }

                    dzs.push({
                        element,
                        dropInfo: {
                            layout_properties: { grid_position: onlyGridPos },
                            minChildIdx: dropping.minChildIdx,
                            maxChildIdx: dropping.maxChildIdx,
                        },
                    });
                }
            }

            return dzs;
        },
        disableDropMode() {
            const { panelId, cols } = this._anvil;
            const element = this.anvil$hooks.domElement!;
            const dzs = element.querySelectorAll<HTMLElement>(`[anvil-column-panel-dropzone].belongs-to-${panelId}`);
            for (const dz of dzs) {
                dz.remove();
            }
            element.classList.remove("anvil-column-panel-with-dzs");
        },
        getInteractions() {
            const oldHandles = this._anvil.getHandles();
            const domElement = this.anvil$hooks.domElement!;
            const interactions = oldHookSpec.getInteractions!.call(this);

            const handleInteractions = oldHandles.map((h: any): HandleInteraction => {
                const { x, y, width, height } = h;
                let grabData: any = null;
                const elementBounds = domElement.getBoundingClientRect();
                return {
                    type: "handle",
                    x: x - elementBounds.x - (document.scrollingElement?.scrollLeft || 0) + 4,
                    y: y - elementBounds.y - (document.scrollingElement?.scrollTop || 0) + 4,
                    width,
                    height,
                    direction: "x",
                    callbacks: {
                        grab: () => {
                            grabData = this._anvil.handleGrab(h, 0, 0);
                        },
                        drag: (dx, dy, metaKey) => {
                            const handle = this._anvil.handleDrag(h, grabData, dx, dy, metaKey);
                            const elementBounds = domElement.getBoundingClientRect();
                            return {
                                x: handle.x - elementBounds.x - (document.scrollingElement?.scrollLeft || 0) + 4,
                                y: handle.y - elementBounds.y - (document.scrollingElement?.scrollTop || 0) + 4,
                            };
                        },
                        drop: (dx, dy) => {
                            const props = this._anvil.handleDrop(h, grabData, dx, dy);
                            const propertyUpdates: any = props.properties["undefined"]; // Because the component doesn't know its own name anymore.
                            this._anvil.setPropJS("col_widths", propertyUpdates["col_widths"]);
                            designerApi.updateComponentProperties(this, propertyUpdates, {});
                            designerApi.notifyInteractionsChanged(this);
                        },
                        doubleClick: () => {
                            const props = this._anvil.handleDoubleClick(h);
                            const propertyUpdates: any = props.properties["undefined"]; // Because the component doesn't know its own name anymore.
                            this._anvil.setPropJS("col_widths", propertyUpdates["col_widths"]);
                            designerApi.updateComponentProperties(this, propertyUpdates, {});
                            designerApi.notifyInteractionsChanged(this);
                        },
                    },
                };
            });

            interactions.push(...handleInteractions);

            return interactions;
        },
    };
};

export const setupDesignColumnPanelV3 = (self: Component, kws?: Kws) =>
    chainOrSuspend(
        DesignColumnPanel.prototype.tp$init.call(self, [], kws),
        () => {
            var maxChildIdxOf = function (element: HTMLElement): number {
                var maxChildIdx = -1;
                element
                    .querySelectorAll<HTMLElement>(
                        "[data-anvil-designer-columnpanel-component].belongs-to-" + self._anvil.panelId
                    )
                    .forEach((c) => {
                        maxChildIdx = Math.max(parseInt(c.dataset.anvilDesignerPanelChildIdx!), maxChildIdx);
                    });
                return maxChildIdx;
            };

            var minChildIdxOf = function (element: HTMLElement): number {
                var minChildIdx = Number.MAX_VALUE;
                element
                    .querySelectorAll<HTMLElement>(
                        "[data-anvil-designer-columnpanel-component].belongs-to-" + self._anvil.panelId
                    )
                    .forEach((c) => {
                        minChildIdx = Math.min(parseInt(c.dataset.anvilDesignerPanelChildIdx!), minChildIdx);
                    });
                return minChildIdx;
            };

            var buildGridPath = function (colElement: any) {
                let path = "";

                let col = colElement;
                while (col?.classList.contains("anvil-panel-col")) {
                    const row = col.parentElement;

                    path = row.dataset.anvilRowId + "," + col.dataset.anvilColId + " " + path;
                    col = row.parentElement;
                }

                return path.trim();
            };

            const element = self.anvil$hooks.domElement!;
            const { panelId, cols } = self._anvil;

            const mkDropzone = () => {
                const element = document.createElement("div");
                element.classList.add(`belongs-to-${panelId}`);
                element.setAttribute("anvil-column-panel-dropzone", "true");
                return element;
            };

            self._anvil.designerHelpers = {
                mkDropzone,
                maxChildIdxOf,
                minChildIdxOf,
                buildGridPath,
            };

            return pyNone;
        },
        () => self
    );

export const DesignColumnPanelV3 = Sk.misceval.buildClass(
    BASE_MODULES["anvil"],
    function ($gbl, $loc) {
        $loc["__new__"] = PyDefUtils.funcFastCall((args: [ComponentConstructor], kws?: Kws) => {
            const n = DesignColumnPanel.tp$getattr(new pyStr("__new__"));
            const self: Component = Sk.misceval.callsimArray(n, args, kws);

            return setupDesignColumnPanelV3(self, kws);
        });
        const oldHookSpec = ColumnPanel.prototype.anvil$hookSpec;
        $loc.anvil$hookSpec = Object.assign(Object.create(oldHookSpec), mkDesignerHookSpec(oldHookSpec));
    },
    "DesignColumnPanelV3",
    [DesignColumnPanel]
) as ComponentConstructor;
